import { IAIConfiguration } from "@entities/AIControls/lib/types";
import { SORT_ORDER } from "@entities/brief/lib/constants";
import { influencerSuggestStatusMap } from "../constants";
import { Agency } from "./agency";
import { IBrand, IUser } from "./brandUser";
import { IBriefCampaignBriefInfluencer } from "./briefCampains";
import {
  IBriefDeliverable,
  IBriefInfluencerSearchRequestV2,
  IBriefInfluencerV2,
  IDataFieldValue,
  IFieldConfig,
} from "./briefInfluencerV2";
import { SocialMedia } from "./influencer";

interface Brand {
  _id: string;
  name: string;
  logoUrl: string;
  guidelines: string;
  brandOrganisationId?: string;
}

export interface IPlanningOrPricing {
  createdAt: string;
  updatedAt: string;
  status: string;
  progress: number;
  creatorsRequired?: number;
  deadline?: string;
  closedOn?: string;
  noOfCreatorsSuggested?: number;
  noOfCreatorsWithPrices?: number;
  allocatedTo: {
    userId: string;
    userType: string;
    user: {
      firstName: string;
      lastName: string;
      _id: string;
      role?: string;
    };
    userModelName: string;
  }[];
  requirements: string;
  _id: string;
}

interface HistoryEntry {
  createdOn: string;
  name: string;
  remarks: string[];
  userId: string;
  userType: string;
}
interface AccountManager {
  _id: string;
  firstName: string;
  lastName: string;
}

export interface IBriefDataFields {
  briefInfluencerId: string;
  dataFields: {
    [key: string]: string;
  };
}

export interface IBrief extends Partial<IAIConfiguration> {
  isCreatorFitCheckEnabled?: boolean;
  formV2: string;
  canInternalTeamTakeOpsActionsOnBehalfOfBrand?: boolean;
  reportId: string;
  mainStatus: string;
  _id: string;
  createdBy: string;
  createdByModelName: string;
  createdByType: string;
  brand: Brand;
  brandId: string;
  brandName: string;
  name: string;
  docLink: string;
  requirements: string;
  prospectBudget: number;
  confidence: number;
  status: string;
  planning: IPlanningOrPricing;
  pricing: IPlanningOrPricing;
  history: HistoryEntry[];
  createdAt: string;
  updatedAt: string;
  __v: number;
  score: number;
  briefId: string;
  accountManagers: AccountManager[];
  visibleBriefTableColumns?: string[];
  visibleBrandCustomFieldsV2: IFieldConfig[];
  visibleBrandPerformanceFieldsV2: IFieldConfig[];
  // dataFields: { key: string; isSpecialField: boolean }[];
  totalInfluencerCount: number;
  internalGoogleSheetId?: string;
  pricingGoogleSheetId?: string;
  accountManagerGoogleSheetId?: string;

  addCreatorsGoogleSheetId?: string;
  editCreatorsGoogleSheetId?: string;
  dataFieldsV2?: IFieldConfig[];
  customFieldsV2?: IFieldConfig[];
  operationsCustomFieldsV2?: IFieldConfig[];
  visibileOperationsCustomFieldsV2: IFieldConfig[];
  discoveryCounts: number;
  pricingCounts: number;
  purchasedCounts: number;
  operations: IOperations;
  lockedAmount: number;
  defaultDeliverableV2?: IBriefDeliverable;
  defaultDeliverableV2Id?: string;
  visibilityOffAgencies?: string[];
  canShareWithAgency?: boolean;
  form: IBriefForm;
  brandGoogleSheetId?: string;
  suggestedCount: number;
  hiredCount: number;
  liveCount: number;
  brandOrganisationManagers: {
    userId: string;
    userType: string;
    user: {
      firstName: string;
      lastName: string;
      _id: string;
      role?: string;
      emailId?: string;
    };
    userModelName: string;
  }[];
  myPricingTracker?: IBriefPricingTracker[];
  defaultPricingPoc?: {
    userId: string;
    userType: string;
    user: Pick<IUser, "_id" | "emailId" | "firstName" | "lastName" | "role">;
    userModelName: string;
  };
  defaultAgencyPricingPoc?: {
    userId: string;
    userType: string;
    user: Pick<IUser, "_id" | "emailId" | "firstName" | "lastName" | "role">;
    userModelName: string;
  };
  aiOverview?: string;
  pricingOutreachTerms?: string;
}

export interface IBriefPricingTracker {
  userId: string;
  userType: string;
  user: IUser;
  userModelName: string;
  briefStatus: string;
  deadlineFrom: string;
  deadlineTo: string;
  pendingCounts: number;
  allocatedCounts: number;
  closuresCounts: number;
  pricedCounts: number;
}

interface Pagination {
  totalCount: number;
  totalPages: number;
  currentPage: number;
  limit: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface IBriefResponse {
  data: IBrief[];
  pagination: Pagination;
}

export interface IBriefRequest {
  pageNo: number;
  limit: number;
  prospectBudget?: number;
  confidence?: number;
  status?: string[];
  planningStatus?: string[];
  pricingStatus?: string[];
  operationStatus?: string[];
  searchQuery?: string;
  campaignByBrandIds?: string[];
  accountManagersBrandIds?: string[];
  pricingAllocatedTo?: string[];
  planningAllocatedTo?: string[];
  operationsAllocatedTo?: string[];
  sortBy?: { [key: string]: SORT_ORDER | undefined };
  mainStatus?: string[];
  myPricingTracker?: {
    userId: string;
    status?: string;
    closureStatus?: BriefClosureStatus;
  };
}

export enum BriefClosureStatus {
  Open = "Open",
  Closed = "Closed",
}

export interface IBriefCreateRequest {
  brandId: string;
  name: string;
  docLink?: string;
  requirements?: string;
  prospectBudget: number;
  confidence: number;
}

export interface IBriefUpdateRequest {
  brandId: string;
  name: string;
  docLink: string;
  requirements: string;
  prospectBudget: number;
  confidence: number;
  briefId: string;
  status: "Open" | "Closed" | "Pending";
}

export interface IBriefPlanningUpdateRequest {
  briefId: string;
  status: string;
  requirements: string;
  creatorsRequired: number;
  deadline: string;
  allocatedTo: string[];
}
export interface IBriefPricingUpdateRequest {
  briefId: string;
  status: string;
  requirements: string;
  deadline: string;
  allocatedTo: string[];
}
export interface IBriefOperationsUpdateRequest {
  briefId: string;
  status: string;
  allocatedTo: string[];
}

interface Status {
  _id: string;
  count: number;
}

interface Brand {
  _id: string;
  name: string;
}

interface CampaignBy {
  _id: string;
  count: number;
  brand: Brand;
  name: string;
}

interface User {
  _id: string;
  firstName: string;
  lastName: string;
}

interface AccountManager {
  _id: string;
  brandIds: string[];
  count: number;
  user: User;
}

interface AllocatedTo {
  _id: string;
  user: User;
}
export interface IBriefFacets {
  status: Status[];
  planningStatus: Status[];
  pricingStatus: Status[];
  operationStatus: Status[];
  campaignBy: CampaignBy[];
  accountManagers: AccountManager[];
  planningAllocatedTo: AllocatedTo[];
  pricingAllocatedTo: AllocatedTo[];
  operationsAllocatedTo: AllocatedTo[];
  mainStatus: { _id: string; count: number }[];
  inBriefs: number;
  inExecution: number;
  closed: number;
  all: number;
}

interface SocialMediaObject {
  socialMediaObjectId: string;
  estViews: number;
}

export interface ISuggestInfluencerRequest {
  briefId: string;
  addBySocialMediaObjectIds: SocialMediaObject[];
}
export interface ISuggestInfluencerWithPricingRequest {
  briefId: string;
  briefInfluencerIds: string[];
  copyType: CopyType;
  selectAll?: boolean;
  uncheckList?: string[];
}
export interface ISuggestInfluencerUsernameRequest {
  briefId: string;
  addBySocialMediaHandle?: {
    link: string;
    platform: string;
    username: string;
    followers: 1990;
    estViews: number;
  }[];
  addBySocialMediaObjectIds?: {
    socialMediaObjectId: string;
    estViews: number;
  }[];
}

export interface IDeleteInfluencerRequest {
  deals: {
    briefInfluencerId: string;
  }[];
}

export interface IBRIEF_CONFIG_FACET {
  _id: string;
  isSpecailField: boolean;
}

interface MediaRights {
  isMediaRights: boolean;
  rights?: string;
  duration?: string;
}

interface Deal {
  briefInfluencerId: string;
  mediaRights?: MediaRights;
  deliverables: string;
  purchaseCost: number;
  estViews: number;
}

export interface IAddInfluencerDealRequest {
  deals: Deal[];
}

export interface INegotiateInfluencerDealRequest {
  deals: Deal[];
}

export interface ILockDealRequest {
  deals: [
    {
      briefInfluencerId: string;
      purchaseCost: number;
    },
  ];
}

export interface IRejectInfluencerRequest {
  deals: [
    {
      briefInfluencerId: string;
    },
  ];
  planningAllocatedTo: AllocatedTo[];
  pricingAllocatedTo: AllocatedTo[];
}

export interface ILookup {
  isInfluencerAlreadyInBreif: boolean;
  avgReelViews: number;
  engRate: number;
  avgLikes: number;
  avgComments: number;
  avgShares: number;
  avgViews: number;
  username: string;
  platform: string;
  profilePicUrl: string;
}
export interface ILookupV2 {
  isInfluencerAlreadyInBreif: boolean;
  briefInfluencer: IBriefInfluencerV2;
}

export interface IAddBrandPriceRequest {
  deals: { briefInfluencerId: string; sellingCost: number }[];
}

export interface IEditBrandPriceRequest {
  deals: { briefInfluencerId: string; sellingCost: number }[];
}

export interface IAddNewBrandPriceRequest {
  deals: { briefInfluencerId: string; sellingCost: number }[];
}

export interface IShortListBrandInfluencerRequest {
  deals: { briefInfluencerId: string }[];
}
export interface IRejectBrandInfluencerRequest {
  deals: { briefInfluencerId: string }[];
}
export interface IPurchaseBrandInfluencerRequest {
  deals: { briefInfluencerId: string }[];
}
export interface INegotiateBrandInfluencerRequest {
  deals: { briefInfluencerId: string; targetSellingCost: number }[];
}

export interface IAddNewBrandPurchaseTargetPriceRequest {
  deals: { briefInfluencerId: string; targetPurchaseCost: number }[];
}

export interface IOperations {
  allocatedTo: {
    userId: string;
    userType: string;
    user: {
      _id: string;
      firstName: string;
      lastName: string;
      role?: string;
    };
    userModelName: string;
  }[];
  status: string;
  updatedAt: string;
}

export interface IInfluencerByAgency {
  _id: string;
  brief: IBrief;
  briefId: string;
  brand: IBrand;
  brandId: string;
  socialMedia: SocialMedia;
  socialMediaObjectId: string;
  deliverable: IBriefDeliverable;
  agencyId: string;
  agency: Agency;
  suggestedbyType: "Agency";
  status: keyof typeof influencerSuggestStatusMap;
  purchaseCost: {
    cost: number;
  };
  agencyName: string;
  influencerName: string;
  type: "influencer";
  approvedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  approvedAt: string;
}

export interface ISharedByAgency {
  _id: {
    agencyId: string;
  };
  count: number;
  docs: IInfluencerByAgency[];
  agency: Agency;
  type: "agency";
}

export interface IBriefForm {
  platform?: string[];
  description?: string;
  objectives?: string[];
  productsServicesToPromote?: { name: string; url?: string }[];
  creatorsRequired?: number;
  overallBudget?: number;
  overallBudgetCurrency?: string;
  creatorFollowersTypes?: { name?: string; from: number; to: number }[];
  creatorGender?: string[];
  creatorCategory?: string[];
  creatorLocation?: { googlePlaceId?: string; description?: string }[];
  creatorLanguage?: string[];
  avgViews?: { min?: number; max?: number };
  engRate?: number;

  targetCpv?: number;
  audienceAgeRange?: { min?: number; max?: number };
  audienceLocation?: { googlePlaceId?: string; description?: string }[];
  audienceGender?: string[];
  requirements?: string;
  isOverviewCompleted?: boolean;
  isInfluencerRequirementsCompleted?: boolean;
  isDeliverablesCompleted?: boolean;
  brandGuidelines?: string;
  campaignGuidelinesList: string[];
  isCampaignGuidelinesList?: boolean;
  attachements?: {
    url: string;
    type: string;
  }[];
}

export interface IEditDefaultPricicingPocRequest {
  briefId: string;
  pricingPocUserId: string;
}

export interface IRemoveDefaultPricicingPocRequest {
  briefId: string;
  pricingPocUserId: string;
}

export const enum BriefNotificationStatusType {
  Pending = "Pending",
  Completed = "Completed",
  Failed = "Failed",
  Processing = "Processing",
  CompletedResolved = "Completed-Resolved",
  FailedResolved = "Failed-Resolved",
}

export enum BriefNotificationActionType {
  copyToAnotherBrief = "copyToAnotherBrief",
  declineBrand = "declineBrand",
  declineInfluencer = "declineInfluencer",
  setCustomColumns = "setCustomColumns",
  setCustomColumnsOnCampaignInfluencer = "setCustomColumnsOnCampaignInfluencer",
  attachDeliverables = "attachDeliverablesV2",
  purchase = "purchase",
  priceReachout = "priceReachout",
  priceReachoutResend = "priceReachoutResend",
  analyzeCreator = "analyzeBriefFitCheck",
  reAnalyzeCreator = "reAnalyzeBriefFitCheck",
  assignPricingPoc = "assignPricingPoc",
  reallocatePricingPoc = "reallocatePricingPoc",
  removePricingPoc = "removePricingPoc",
  deleteInfluencer = "deleteInfluencer",
  addInfluencersToBucket = "addInfluencersToBucket",
  skipConfirmation = "skipConfirmation",
  resendConfirmation = "resendConfirmation",
  shortlist = "shortlist",
  unshortlist = "unshortlist",
  markPendingReviewAsSeen = "markPendingReviewAsSeen",
  addSocialMediaToBrief = "addSocialMediaToBrief",
  editBriefInfluencer = "editBriefInfluencer",
  attachBriefToCampaignInfluencerTask = "attachBriefToCampaignInfluencerTask",
  assignOperationsPocToCampaignInfluencer = "assignOperationsPocToCampaignInfluencer",
  editOrAddScriptToCampaignInfluencerTask = "editOrAddScriptToCampaignInfluencerTask",
  markPendingReviewAsSeenOnCampaignInfluencer = "markPendingReviewAsSeenOnCampaignInfluencer",
}

export enum CopyType {
  COPY_ONLY = "copy-only",
  COPY_WITH_DELIVERABLES = "copy-with-deliverables",
  COPY_WITH_DELIVERABLES_AND_PRICE = "copy-with-deliverables-and-costs",
}

export interface IBriefNotification {
  _id: string;
  briefId: string;
  actionType: BriefNotificationActionType;
  actionParams: {
    copyType?: CopyType;
    copyToBriefId?: string;
  };
  status: BriefNotificationStatusType;
  createdAt: string;
  updatedAt: string;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  result: {
    status: "success" | "failed";
    reason: string;
    briefInfluencer: IBriefInfluencerV2;
    oldBriefInfluencer?: IBriefInfluencerV2;
    campaignInfluencer?: IBriefCampaignBriefInfluencer;
    socialMedia?: {
      _id: string;
      platform: string;
      username: string;
      link: string;
      profilePicUrl: string;
      id: string;
    };
  }[];
  __v: number;
}

export interface IPreCheckDeliverablesRequest {
  briefInfluencerIds?: string[];
  notIncludeIds?: string[];
  isSelectAll?: boolean;
  filterParams?: Partial<IBriefInfluencerSearchRequestV2>;
  deliverableV2Id: string;
}

export interface IBriefInfluencerPurchaseStatusRequest {
  briefInfluencerIds?: string[];
  notIncludeIds?: string[];
  isSelectAll?: boolean;
  filterParams?: Partial<IBriefInfluencerSearchRequestV2>;
}

export interface IBulkActionRequest {
  briefId: string;
  actionType: string;
  filterParams: Partial<IBriefInfluencerSearchRequestV2>;
  isSelectAll?: boolean;
  actionParams: {
    copyType?: CopyType;
    copyToBriefId?: string;
    deliverableV2Id?: string;
    dataFields?: { [key: string]: IDataFieldValue };
    resetCost?: boolean;

    assignPricingPocParams?: {
      assignedToUserId: string;
      briefId: string;
      deadlineFrom: string;
      deadlineTo: string;
      pricingOutreachTerms?: string;
    };
    removePricingPocParams?: {
      briefId: string;
    };
    reallocatePricingPocParams?: {
      reallocatedToUserId: string;
      briefId: string;
      deadlineFrom: string;
      deadlineTo: string;
      pricingOutreachTerms?: string;
    };
    declineInfluencerParams?: {
      reason: string;
    };
    addInfluencersToBucketParams?: {
      bucketId: string;
    };
    attachBriefToCampaignInfluencerTaskParams?: {
      taskId: string;
      form: {
        title?: string;
      };
    };
    assignedToUserId?: string;
    editOrAddScriptToCampaignInfluencerTaskParams?: {
      taskId: string;
      copyFromScriptId: string;
    };
  };
  ids: string[];
  notIncludeIds?: string[];

  singleActionParams?: {
    addSocialMediaToBriefParams?: Partial<IAddSocialMediaToBriefParams>;
    editBriefInfluencerParams?: Partial<IEditBriefInfluencersParams>;
  }[];

  handleSuccess?: (
    data: { id: string; briefInfluencer: IBriefInfluencerV2 }[]
  ) => void;
}

export enum BreifInfluencerV2MainStatusEnum {
  // Suggested = "Suggested",
  Brief = "Brief",
  Campaign = "Campaign",
  Cancelled = "Cancelled",
  Withdrawn = "Withdrawn",
  Deleted = "Deleted",
  Completed = "Completed",
}

export interface IBriefAiOverviewResponse {
  brandId: string;
  briefId: string;
  parsedResponse: string;
  status: BriefAiOverviewStatus;
}
export interface IBriefAiOverviewGenerateResponse {
  _id: string;
}
export enum BriefAiOverviewStatus {
  Pending = "Pending",
  Completed = "Completed",
  Failed = "Failed",
}

export interface IBriefInflunecerPendingReviewFacets {
  brand: {
    agencySuggested: number;
    agencyDeclined: number;
    agencyAddedNotes: number;
    agencyAddNegoCost: number;
    total: number;
    briefInfluencerCount: number;
  };
  agency: {
    brandShortlisted: number;
    brandDeclined: number;
    brandSuggested: number;
    brandPurchased: number;
    brandNegoRequested: number;
    brandChangedDeliverable: number;
    brandAddedNotes: number;
    total: number;
    briefInfluencerCount: number;
  };
}

export interface IParseCreatorsGoogleSheetRequest {
  socialMediaObjectId: string;
  rowNumber: number;
  link: string;
  platform: string;
  message: string;
  status: "success" | "failed";
  username: string;
  editedDataFields?: { [key: string]: string | number };
  deliverablesV2Id?: string;
  influencerCost?: number;
  brandCost?: number;
}

export interface IParseEditCreatorsGoogleSheetRequest {
  rowNumber: number;
  briefInfluencerId: string;
  editDeliverablesV2Id: string;
  errors?: string[];
  status: "success" | "failed";
  editedDataFields: { [key: string]: string | number };
  brandCost?: number;
  influencerCost?: number;
  platform?: string;
  username?: string;
}
export type IEditBriefInfluencersParams = Omit<
  IParseEditCreatorsGoogleSheetRequest,
  "rowNumber" | "status" | "errors" | "platform" | "username"
>;

export type IAddSocialMediaToBriefParams = Omit<
  IParseCreatorsGoogleSheetRequest,
  "rowNumber" | "status" | "message"
>;

export interface IParseCreatorsCsvRequest {
  briefId: string;
  csv: string;
}
